<div class="absolute inset-0 flex items-center justify-center p-4">
    <button type="button"
        class="absolute inset-0 bg-zinc-200/40 dark:bg-zinc-900/60 backdrop-blur-md"
        (click)="dismiss()"
        aria-label="Dismiss add to group"></button>

    <div role="dialog" aria-modal="true" aria-labelledby="add-to-group-title"
        class="relative z-10 flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-2xl primary-card-container">
        <div class="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div>
                @if (viewMode() === 'view') {
                <h2 id="add-to-group-title" class="text-sm font-semibold text-primary">Add to group</h2>
                <p class="text-xs text-secondary">Click a group to add {{ creator().firstName }}</p>
                } @else {
                <h2 id="add-to-group-title" class="text-sm font-semibold text-primary">Create new group</h2>
                <p class="text-xs text-secondary">Then {{ creator().firstName }} will be added to it</p>
                }
            </div>
            <div class="flex shrink-0 items-center gap-2">
                @if (viewMode() === 'view') {
                <button (click)="showCreateForm()" type="button"
                    class="secondary-button rounded-full px-2.5 py-1 text-xs font-semibold text-primary">Create new group</button>
                } @else {
                <button (click)="showGroupList()" type="button"
                    class="secondary-button rounded-full px-2.5 py-1 text-xs font-semibold text-secondary">Back</button>
                }
                <button type="button" (click)="dismiss()"
                    class="rounded-full bg-zinc-200 p-1.5 text-primary hover:bg-zinc-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-600 dark:bg-zinc-600 dark:hover:bg-zinc-500"
                    aria-label="Close add to group">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="size-5" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>

        @if (viewMode() === 'view') {
        <div class="min-h-0 flex-1 overflow-y-auto p-2">
            @if (groupsLoadState() === 'loading') {
            <p class="px-3 py-8 text-center text-sm text-secondary">Loading groups...</p>
            } @else if (groupsLoadState() === 'error') {
            <p class="px-3 py-8 text-center text-sm text-secondary">Could not load groups. Close and try again.</p>
            } @else if (groupList().length === 0) {
            <div class="flex flex-col items-center justify-center px-4 py-10 text-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"
                    class="size-12 text-secondary">
                    <path
                        d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                        stroke-width="2" vector-effect="non-scaling-stroke" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
                <h3 class="mt-2 text-sm font-semibold text-primary">No groups yet</h3>
                <p class="mt-1 text-xs text-secondary">Create a group to start adding creators.</p>
                <button (click)="showCreateForm()" type="button"
                    class="primary-button mt-4 inline-flex items-center rounded-md px-3 py-2 text-xs font-semibold">
                    Create new group
                </button>
            </div>
            } @else {
            <ul role="list" class="divide-y divide-gray-100 dark:divide-zinc-800">
                @for (group of groupList(); track group.id) {
                <li>
                    <button type="button" (click)="addCreatorToGroup(group)" [disabled]="updatingGroup()"
                        class="flex w-full items-center justify-between gap-3 rounded-lg p-3 text-left hover:bg-gray-50 disabled:cursor-wait disabled:opacity-60 dark:hover:bg-zinc-800"
                        [attr.aria-pressed]="creatorIncludedInGroup(group.id)">
                        <div class="min-w-0">
                            <p class="truncate text-sm font-semibold text-primary">{{ group.groupName }}</p>
                            <p class="truncate text-xs text-secondary">{{ visibilityLabel(group) }}</p>
                        </div>
                        @if (creatorIncludedInGroup(group.id)) {
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                            stroke="currentColor" class="size-5 shrink-0 text-green-500 dark:text-green-400"
                            aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                        }
                    </button>
                </li>
                }
            </ul>
            }
        </div>
        } @else {
        <div class="min-h-0 flex-1 overflow-y-auto p-4">
            <div class="space-y-4">
                <div>
                    <label for="add-to-group-name" class="block text-xs font-medium text-primary">Group name</label>
                    <div class="mt-1">
                        <input type="text" id="add-to-group-name" [formField]="recruitGroupForm.groupName"
                            class="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-primary outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary dark:bg-zinc-900 dark:outline-zinc-700 dark:placeholder:text-zinc-500 dark:focus:outline-primary">
                    </div>
                </div>
                <div>
                    <label for="add-to-group-description" class="block text-xs font-medium text-primary">Description
                        <span class="font-normal text-secondary">(optional)</span></label>
                    <div class="mt-1">
                        <textarea rows="3" id="add-to-group-description" [formField]="recruitGroupForm.groupDescription"
                            class="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-primary ring-1 ring-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary dark:bg-zinc-900 dark:ring-zinc-700 dark:placeholder:text-zinc-500 dark:focus:outline-primary"></textarea>
                    </div>
                </div>
                <fieldset class="space-y-3">
                    <legend class="text-xs font-medium text-primary">Who can see this group?</legend>
                    <div class="relative flex items-start">
                        <div class="absolute flex h-6 items-center">
                            <input id="add-to-group-privacy-user" value="user" [formField]="recruitGroupForm.privacy"
                                aria-describedby="add-to-group-privacy-user-description" type="radio"
                                class="relative size-4 appearance-none rounded-full border border-gray-300 before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 dark:border-zinc-700 dark:before:bg-zinc-900 dark:checked:border-primary dark:checked:bg-primary dark:focus-visible:outline-primary forced-colors:appearance-auto forced-colors:before:hidden">
                        </div>
                        <div class="pl-7 text-xs">
                            <label for="add-to-group-privacy-user" class="font-medium text-primary">Just me</label>
                            <p id="add-to-group-privacy-user-description" class="text-secondary">Only you will see this group.</p>
                        </div>
                    </div>
                    <div class="relative flex items-start">
                        <div class="absolute flex h-6 items-center">
                            <input id="add-to-group-privacy-brand" value="brand" [formField]="recruitGroupForm.privacy"
                                aria-describedby="add-to-group-privacy-brand-description" type="radio"
                                class="relative size-4 appearance-none rounded-full border border-gray-300 before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 dark:border-zinc-700 dark:before:bg-zinc-900 dark:checked:border-primary dark:checked:bg-primary dark:focus-visible:outline-primary forced-colors:appearance-auto forced-colors:before:hidden">
                        </div>
                        <div class="pl-7 text-xs">
                            <label for="add-to-group-privacy-brand" class="font-medium text-primary">Share with my team</label>
                            <p id="add-to-group-privacy-brand-description" class="text-secondary">Members of this brand can access this group.</p>
                        </div>
                    </div>
                    <div *appHasRole="[6]" class="relative flex items-start">
                        <div class="absolute flex h-6 items-center">
                            <input id="add-to-group-privacy-global" value="global" [formField]="recruitGroupForm.privacy"
                                aria-describedby="add-to-group-privacy-global-description" type="radio"
                                class="relative size-4 appearance-none rounded-full border border-gray-300 before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 dark:border-zinc-700 dark:before:bg-zinc-900 dark:checked:border-primary dark:checked:bg-primary dark:focus-visible:outline-primary forced-colors:appearance-auto forced-colors:before:hidden">
                        </div>
                        <div class="pl-7 text-xs">
                            <label for="add-to-group-privacy-global" class="font-medium text-primary">Global group</label>
                            <p id="add-to-group-privacy-global-description" class="text-secondary">This will be a globally accessible group.</p>
                        </div>
                    </div>
                </fieldset>
            </div>
        </div>
        <div class="flex shrink-0 justify-end border-t border-border px-4 py-3">
            <button (click)="submitForm()" type="button" [disabled]="submitInProgress() || recruitGroupForm().invalid()"
                class="primary-button inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60">
                @if (submitInProgress()) {
                Saving...
                } @else {
                Save group
                }
            </button>
        </div>
        }
    </div>
</div>
